/**
 * What a region shows when it holds nothing yet.
 *
 * Roughly forty-five of these had been written inline, most of them a single
 * muted sentence, and none offering the action that would fill the region. An
 * empty state has a job: say what belongs here, why it is empty, and what to do
 * about it. The `action` is optional only because some regions genuinely fill
 * themselves.
 *
 * @module components/ui/EmptyState
 */

import React, { type ReactNode } from "react";

import { Icon } from "./Icon";
import { Heading } from "./Text";

export type EmptyStateDensity = "default" | "row" | "inline" | "pane";

const LAYOUT: Record<EmptyStateDensity, string> = {
  default: "flex-col py-10 px-6 text-center",
  row: "flex-col py-6 px-4 text-center",
  inline: "flex-row flex-wrap py-3 px-4 text-start",
  pane: "flex-col flex-1 justify-center py-10 px-6 text-center",
};

const SURFACE: Record<EmptyStateDensity, string> = {
  default: "border border-dashed border-base-300 bg-base-100",
  row: "border border-dashed border-base-300 bg-base-100",
  inline: "border border-dashed border-base-300 bg-base-100",
  pane: "bg-surface-subtle",
};

export interface EmptyStateProps {
  /** What is missing, as a short noun phrase: "No review rounds yet". */
  title: string;
  /**
   * Renders the title as a heading at this level, for a screen that is nothing
   * but an empty state and so has no other `h1`. A `<p>` by default: an empty
   * state inside a panel must not inject a heading into the page's outline.
   */
  headingLevel?: 1 | 2 | 3 | 4;
  /** Why it is empty and what fills it. */
  description?: ReactNode;
  /** The one thing to do next. */
  action?: ReactNode;
  icon?: string;
  /**
   * `row` fits inside a table cell or a narrow column. `inline` is one line
   * of icon, title and description, for a card that is empty far more often
   * than not. `pane` fills a pane that has nothing selected: no dashed box,
   * centred in the height it is given.
   */
  density?: EmptyStateDensity;
  className?: string;
  "data-testid"?: string;
}

/**
 * @example
 * ```tsx
 * <EmptyState
 *   title="No review rounds yet"
 *   description="Submit this programme for internal review to open the first round."
 *   action={<Button variant="primary" onClick={submit}>Submit for review</Button>}
 * />
 * ```
 */
export function EmptyState({
  title,
  headingLevel = undefined,
  description = undefined,
  action = undefined,
  icon = undefined,
  density = "default",
  className = "",
  "data-testid": testId,
}: EmptyStateProps): React.ReactElement {
  return (
    <div
      className={`flex items-center gap-2 rounded-box ${SURFACE[density]} ${LAYOUT[density]} ${className}`.trim()}
      data-testid={testId}
    >
      {icon && (
        <Icon
          name={icon}
          className={`${density === "inline" ? "text-lg" : "text-2xl"} text-muted`}
          aria-hidden
        />
      )}
      {headingLevel ? (
        <Heading level={headingLevel} size="md">
          {title}
        </Heading>
      ) : (
        <p className="text-sm font-semibold text-base-content">{title}</p>
      )}
      {description && (
        <p className={`${density === "inline" ? "" : "max-w-[52ch] "}text-md text-muted`}>
          {description}
        </p>
      )}
      {/* `empty:hidden`: a lock can leave the action rendering nothing. */}
      {action && <div className="mt-2 empty:hidden">{action}</div>}
    </div>
  );
}
